import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
} from "@mui/material";
import React, { useState } from "react";

interface MonthlyData {
  [key: string]: number;
}

interface DailyData {
  [key: string]: number;
}

interface SalesDataType {
  flight: {
    months: MonthlyData;
    days: DailyData;
  };
  ride: {
    months: MonthlyData;
    days: DailyData;
  };
}

interface SalesChartProps {
  title: string;
  subtitle: string;
  data: SalesDataType["flight"] | SalesDataType["ride"];
}

const salesData: SalesDataType = {
  flight: {
    months: {
      January: 1000,
      February: 800,
      March: 700,
      April: 900,
      May: 1500,
      June: 500,
      July: 600,
      August: 1000,
      September: 900,
      October: 800,
      November: 1000,
      December: 800,
    },
    days: {
      Monday: 150,
      Tuesday: 180,
      Wednesday: 200,
      Thursday: 170,
      Friday: 220,
      Saturday: 130,
      Sunday: 100,
    },
  },
  ride: {
    months: {
      January: 1000,
      February: 800,
      March: 700,
      April: 900,
      May: 1500,
      June: 500,
      July: 600,
      August: 1000,
      September: 900,
      October: 800,
      November: 1000,
      December: 800,
    },
    days: {
      Monday: 180,
      Tuesday: 160,
      Wednesday: 190,
      Thursday: 200,
      Friday: 250,
      Saturday: 140,
      Sunday: 110,
    },
  },
};

const SalesChart: React.FC<SalesChartProps> = ({ title, subtitle, data }) => {
  const [viewMode, setViewMode] = useState<"months" | "days">("months");

  const currentData = viewMode === "months" ? data.months : data.days;
  const maxValue = Math.max(
    ...Object.values(currentData as Record<string, number>),
    0,
  );

  return (
    <Card
      sx={{
        padding: 0,
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        "&:hover": {
          boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ padding: 0, overflowX: "auto" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
          p={3}
          sx={{ borderBottom: "1px solid #E4E7EC", pb: 2, width: "100%" }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#101828",
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#475467" }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <Box p={3} pt={0} minWidth={{ xs: "480px", sm: "auto" }}>
          <ButtonGroup
            size="small"
            sx={{
              backgroundColor: "#e5e7eb42",
              p: 0.5,
              borderRadius: "12px",
              mb: 3,
            }}
          >
            <Button
              onClick={() => setViewMode("months")}
              sx={{
                backgroundColor:
                  viewMode === "months" ? "#379E66" : "transparent",
                color: viewMode === "months" ? "white" : "#475467",
                textTransform: "none",
                fontWeight: 400,
                fontSize: "14px",
                px: 2,
                py: 0.75,
                border: "1px solid transparent",
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor:
                    viewMode === "months" ? "#34aa69ff" : "#F9FAFB",
                  border: "1px solid #E5E7EB",
                },
              }}
            >
              Months
            </Button>
            <Button
              onClick={() => setViewMode("days")}
              sx={{
                backgroundColor:
                  viewMode === "days" ? "#379E66" : "transparent",
                color: viewMode === "days" ? "white" : "#475467",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                px: 2,
                py: 0.75,
                border: "1px solid transparent",
                borderRadius: "12px",
                borderLeft: "none",
                "&:hover": {
                  backgroundColor:
                    viewMode === "days" ? "#34aa69ff" : "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderLeft: "none",
                },
              }}
            >
              Days
            </Button>
          </ButtonGroup>

          <Box>
            {Object.entries(currentData).map(
              ([period, value]: [string, number]) => (
                <Box key={period} display="flex" alignItems="center" mb={2}>
                  <Typography
                    sx={{
                      width: "100px",
                      fontSize: "14px",
                      color: "#000000",
                      fontWeight: 400,
                    }}
                  >
                    {period}
                  </Typography>

                  <Box display="flex" alignItems="center" width="100%" mx={1}>
                    <Box
                      sx={{
                        height: "8px",
                        backgroundColor: "#379E66",
                        borderRadius: "4px",
                        mr: 2,
                        width: `${(value / maxValue) * 90}%`,
                        transition: "width 0.3s ease",
                      }}
                    />

                    <Typography
                      sx={{
                        width: "auto",
                        minWidth: "50px",
                        textAlign: "left",
                        fontSize: "14px",
                        color: "#101828",
                        fontWeight: 500,
                      }}
                    >
                      ₦{value.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              ),
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function SalesBreakdown({ data }: { data?: SalesDataType }) {
  const chartData = data || salesData;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: "24px",
        mb: "36px",
      }}
    >
      <SalesChart
        title="Flight Sales Breakdown"
        subtitle="Track revenue trends over months or days."
        data={chartData.flight}
      />
      <SalesChart
        title="Ride Sales Breakdown"
        subtitle="Track revenue trends over months or days."
        data={chartData.ride}
      />
    </Box>
  );
}
